import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { Button, Input, Alert } from '../ui';
import { useRegister } from '../../hooks/auth';
import { getUserFriendlyErrorMessage } from '../../lib/errorMessages';

const registerSchema = z.object({
  email: z.string().trim().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Must contain at least one number'),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  phone: z
    .string()
    .regex(/^[6-9]\d{9}$/, 'Invalid Indian phone number')
    .optional()
    .or(z.literal('')),
  referralCode: z
    .string()
    .length(8, 'Referral code must be 8 characters')
    .regex(/^[A-Z0-9]{8}$/, 'Referral code must contain only uppercase letters and numbers')
    .optional()
    .or(z.literal('')),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Terms and Privacy Policy' }),
  }),
});

type RegisterFormData = z.infer<typeof registerSchema>;

const SuccessCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2),
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 56,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
}));

const NameGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
  },
}));

export function RegisterForm() {
  const { mutate: register, isPending, isError, error, isSuccess } = useRegister();

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = (data: RegisterFormData) => {
    const payload = {
      ...data,
      phone: data.phone || undefined,
      referralCode: data.referralCode || undefined,
    };
    register(payload);
  };

  if (isSuccess) {
    return (
      <SuccessCard>
        <SuccessIcon />
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Registration Complete
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please check your email to verify your account and complete the sign-up process.
        </Typography>
      </SuccessCard>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {isError && <Alert variant="error">{getUserFriendlyErrorMessage(error)}</Alert>}

      <NameGrid>
        <Input
          label="First Name"
          {...registerField('firstName')}
          error={errors.firstName?.message}
          disabled={isPending}
          autoComplete="given-name"
          fullWidth
        />

        <Input
          label="Last Name"
          {...registerField('lastName')}
          error={errors.lastName?.message}
          disabled={isPending}
          autoComplete="family-name"
          fullWidth
        />
      </NameGrid>

      <Input
        label="Email"
        type="email"
        {...registerField('email')}
        error={errors.email?.message}
        disabled={isPending}
        autoComplete="email"
        fullWidth
      />

      <Input
        label="Phone (Optional)"
        type="tel"
        {...registerField('phone')}
        error={errors.phone?.message}
        disabled={isPending}
        autoComplete="tel"
        placeholder="e.g., 9876543210"
        fullWidth
      />

      <Input
        label="Password"
        type="password"
        {...registerField('password')}
        error={errors.password?.message}
        disabled={isPending}
        autoComplete="new-password"
        helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 number"
        fullWidth
      />

      <Input
        label="Referral Code (Optional)"
        {...registerField('referralCode', {
          setValueAs: (value) => value.toUpperCase(),
        })}
        error={errors.referralCode?.message}
        disabled={isPending}
        placeholder="e.g., CXBM72ZF"
        helperText="Have a referral code? Enter it below to earn rewards."
        fullWidth
        inputProps={{ maxLength: 8, style: { textTransform: 'uppercase' } }}
      />

      <Box>
        <FormControlLabel
          control={<Checkbox {...registerField('acceptedTerms')} disabled={isPending} />}
          label={
            <Typography variant="body2" color="text.secondary">
              I accept the{' '}
              <Link href="/terms" target="_blank" rel="noopener">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="/privacy" target="_blank" rel="noopener">
                Privacy Policy
              </Link>
            </Typography>
          }
        />
        {errors.acceptedTerms && (
          <FormHelperText error>{errors.acceptedTerms.message}</FormHelperText>
        )}
      </Box>

      <Button type="submit" fullWidth isLoading={isPending} glow>
        Create Account
      </Button>
    </Box>
  );
}
